import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { StaffWorkerEditForm } from '../components/forms/StaffWorkerEditForm.js';
import { DataCard } from '../components/DataCard.js';
import { ReviewActions } from '../components/ReviewActions.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { useImpersonation } from '../hooks/useImpersonation.js';
import { requireRole } from '../lib/auth-guard.js';
import { formatAddress, formatBoolean, formatStringList } from '../lib/profile-formatters.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/workers/$id')({
  component: WorkerDetail,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function WorkerDetail() {
  const { id } = Route.useParams();
  const utils = trpc.useUtils();
  const impersonation = useImpersonation();
  const [editing, setEditing] = useState(false);
  const worker = trpc.workers.getById.useQuery({ workerId: id });
  const docs = trpc.documents.listForOwner.useQuery({ ownerType: 'worker', ownerId: id });
  const review = trpc.workers.review.useMutation({
    onSuccess: () => {
      utils.workers.getById.invalidate({ workerId: id });
      utils.workers.list.invalidate();
      utils.admin.overview.invalidate();
    },
  });
  const reviewDoc = trpc.documents.review.useMutation({
    onSuccess: () => utils.documents.listForOwner.invalidate({ ownerType: 'worker', ownerId: id }),
  });

  if (worker.isLoading) {
    return (
      <AdminLayout>
        <p className="text-[var(--muted-foreground)]">Lade …</p>
      </AdminLayout>
    );
  }
  if (worker.error || !worker.data) {
    return (
      <AdminLayout>
        <p className="text-[oklch(0.55_0.2_25)]">
          Fehler: {worker.error?.message ?? 'Worker nicht gefunden'}
        </p>
      </AdminLayout>
    );
  }

  const w = worker.data;
  const fullName = [w.firstName, w.lastName].filter(Boolean).join(' ') || '(unbenannt)';

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <Link
            to="/admin/workers"
            className="text-sm text-[var(--muted-foreground)] hover:text-foreground"
          >
            ← Worker-Liste
          </Link>
          <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold">{fullName}</h1>
              <p className="text-[var(--muted-foreground)]">
                Status: <StatusBadge status={w.status} /> · zuletzt geändert{' '}
                {new Date(w.updatedAt).toLocaleString('de-DE')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={impersonation.starting}
              onClick={() => impersonation.startWorker(w.userId)}
              title="Die Sicht dieses Workers übernehmen (auditiert)"
            >
              {impersonation.starting ? 'Wechsle …' : '↪ View übernehmen'}
            </Button>
          </div>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Profil-Review</CardTitle>
            <CardDescription>Stammdaten freigeben, ablehnen oder blockieren</CardDescription>
          </CardHeader>
          <CardContent>
            <ReviewActions
              currentStatus={w.status}
              pending={review.isPending}
              onReview={(action, notes) => review.mutateAsync({ workerId: w.id, action, notes })}
            />
            {w.reviewNotes && (
              <p className="mt-3 text-xs text-[var(--muted-foreground)]">
                Letzte Notiz: {w.reviewNotes}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <DataCard
            title="Persönlich"
            rows={[
              ['Vorname', w.firstName],
              ['Nachname', w.lastName],
              ['Geburtsname', w.maidenName],
              ['Geburtsdatum', w.dateOfBirth],
              ['Geburtsort', w.placeOfBirth],
              ['Staatsangehörigkeit', w.nationality],
              [
                'Adresse',
                formatAddress(w.addressStreet, w.addressZip, w.addressCity, w.addressCountry),
              ],
              ['Telefon', w.phone],
            ]}
          />
          <DataCard
            title="Steuer & Bank"
            rows={[
              ['Steuer-ID', w.taxId],
              ['Steuerklasse', w.taxClass],
              ['Konfession', w.religiousAffiliation],
              ['Familienstand', w.maritalStatus],
              ['Kinder', w.numberOfChildren?.toString()],
              ['Kinderfreibeträge', w.childAllowances?.toString()],
              ['IBAN', w.iban],
              ['BIC', w.bic],
              ['Kontoinhaber', w.bankAccountHolder],
            ]}
          />
          <DataCard
            title="Lohn"
            rows={[
              ['Stundenlohn', w.hourlyWage ? `${w.hourlyWage} ${w.wageCurrency}` : null],
              ['Währung', w.wageCurrency],
            ]}
          />
          <DataCard
            title="SV & Aufenthalt"
            rows={[
              ['SV-Nummer', w.socialSecurityNumber],
              ['Krankenkasse', w.healthInsuranceProvider],
              ['Versicherung', w.insuranceStatus],
              ['Aufenthaltstitel', w.residencePermitType],
              ['Aufenthalt bis', w.residencePermitExpiry],
              ['Erwerbstätigkeit', formatBoolean(w.workAuthorizationConfirmed)],
              ['Beschränkungen', w.workRestrictions],
            ]}
          />
          <DataCard
            title="Disability & Qualifikationen"
            rows={[
              ['Schwerbehindert', formatBoolean(w.severeDisability)],
              ['GdB', w.disabilityDegree?.toString()],
              ['Nachweis gültig bis', w.disabilityValidUntil],
              ['Höchster Abschluss', w.highestEducation],
              ['Führerschein', w.drivingLicenseClass],
              ['Zertifikate', formatStringList(w.professionalCertifications)],
            ]}
          />
          <DataCard
            title="System & Review"
            rows={[
              ['Worker-ID', w.id],
              ['User-ID', w.userId],
              ['Tenant-ID', w.tenantId],
              ['HireBotIQ-Match-ID', w.hirebotiqMatchId],
              ['Interne Notizen', w.notes],
              [
                'Eingereicht',
                w.submittedAt ? new Date(w.submittedAt).toLocaleString('de-DE') : null,
              ],
              ['Geprüft', w.reviewedAt ? new Date(w.reviewedAt).toLocaleString('de-DE') : null],
              ['Geprüft durch', w.reviewedBy],
              ['Review-Notiz', w.reviewNotes],
              ['Angelegt', new Date(w.createdAt).toLocaleString('de-DE')],
            ]}
          />
        </div>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Daten verwalten</CardTitle>
              <CardDescription>
                Stammdaten im Namen des Workers bearbeiten (auditiert)
              </CardDescription>
            </div>
            <Button
              variant={editing ? 'ghost' : 'outline'}
              size="sm"
              onClick={() => setEditing((v) => !v)}
            >
              {editing ? 'Schließen' : 'Bearbeiten'}
            </Button>
          </CardHeader>
          {editing && (
            <CardContent>
              <StaffWorkerEditForm workerId={w.id} />
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dokumente</CardTitle>
            <CardDescription>Pro Dokument: freigeben oder ablehnen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {docs.data?.length === 0 && (
              <p className="text-xs text-[var(--muted-foreground)]">Keine Dokumente hochgeladen.</p>
            )}
            {docs.data?.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--border)] p-3"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-medium truncate">{d.originalFilename}</p>
                  <p className="text-xs text-[var(--muted-foreground)] flex items-center gap-2">
                    <span>{d.type}</span>
                    <StatusBadge status={d.status} />
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`/api/download/${d.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline text-[var(--muted-foreground)] hover:text-foreground"
                  >
                    Ansehen
                  </a>
                  <Button
                    size="sm"
                    disabled={reviewDoc.isPending || d.status === 'approved'}
                    onClick={() => reviewDoc.mutate({ documentId: d.id, action: 'approve' })}
                  >
                    Freigeben
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={reviewDoc.isPending || d.status === 'rejected'}
                    onClick={() => reviewDoc.mutate({ documentId: d.id, action: 'reject' })}
                  >
                    Ablehnen
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
